import { Box, Skeleton } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useCustomBreakpoint, CustomBreakpoints } from './Charts'
import { TableHeaderRow, TableCell, TableHeaderText, TableRow, TableText, NUMBER_COLUMN_MIN_WIDTH } from './commonComponents'
import { formatDollarValue } from './chartUtils'
import { TableContainer } from './TableContainer'
import { getIconUrl } from '../../services/static_data'
import { useStoreState } from '../../model/hooks'
import { getTokenBraavosName } from '../../blockchainUtils'
import { useNavigate } from 'react-router'
import { TokenOverview, loadTokenOverviewData } from './chartsApi'
import { SearchIcon } from '../../svgIcons/SearchIcon'
import { Token } from '../../atoms'
import { SpaceshipIcon } from '../../svgIcons/SpaceshipIcon'
// import { Token } from '../TokenSelectorNew/Select'

type Column = '#' | 'NAME' | 'PRICE' | 'VOLUME' | 'TVL'

export const TokensTable = () => {
    const size = useCustomBreakpoint()
    const tokensList = useStoreState((state) => state.tokens.tokens['mainnet-alpha'])
    const getPrice = useStoreState((state) => state.pricing.getPrice)
    const [tokensOverview, setTokensOverview] = useState([] as (TokenOverview & { address: string })[])
    const [allTokens, setAllTokens] = useState([] as (TokenOverview & { address: string })[])
    const navigate = useNavigate()
    const [searchTerm, setSearchTerm] = useState('')
    const [isInputFocused, setIsInputFocused] = useState(false)
    const [isNoResults, setIsNoResults] = useState(false)

    useEffect(() => {
        onSearchTokens()
    }, [searchTerm])

    useEffect(() => {
        if (tokensList) loadTokensOverview()
    }, [tokensList])

    function getTokenOverview(tokenAddress: string) {
        if (!tokensOverview) return undefined

        return tokensOverview.find((item) => item.address === tokenAddress)
    }

    async function loadTokensOverview() {
        if (!tokensList) return
        const promises: any[] = []
        const keys = Object.keys(tokensList)
        keys.map((tokenAddress) => {
            promises.push(loadTokenOverviewData(tokenAddress))
        })
        const tokenOveriews: (TokenOverview & { address: string })[] = (await Promise.all(promises)).filter((item) => item !== undefined)
        tokenOveriews.map((item, index) => {
            item.address = keys[index]
        })

        tokenOveriews.sort((a, b) => {
            if (a.tvl > b.tvl) return -1
            if (b.tvl > a.tvl) return 1
            return 0
        })

        updatePoolsWithoutBannedTokens(tokenOveriews)
        setAllTokens(tokenOveriews)
    }

    function updatePoolsWithoutBannedTokens(data: (TokenOverview & { address: string })[]) {
        const newData = [] as (TokenOverview & { address: string })[]

        data.map((item) => {
            if (item.address.toLowerCase() !== '0x6f15ec4b6ff0b7f7a216c4b2ccdefc96cbf114d6242292ca82971592f62273b'.toLowerCase()) newData.push(item)
        })
        setTokensOverview(newData)
    }

    function onSearchTokens() {
        if (!tokensOverview || !tokensList) return
        if (!searchTerm) {
            // setTokensOverview(allTokens)
            updatePoolsWithoutBannedTokens(allTokens)
            setIsNoResults(false)
            return
        }

        let tokens: Token[] = []
        if (searchTerm) {
            const searchTermLowerCased = searchTerm.toLowerCase()
            tokens = Object.values(tokensList).filter(
                (option) =>
                    option.address.toLowerCase().includes(searchTermLowerCased) ||
                    option.symbol.toLowerCase().includes(searchTermLowerCased) ||
                    option.name.toLowerCase().includes(searchTermLowerCased)
            )
        }
        const newOverViews: (TokenOverview & { address: string })[] = []
        tokens.map((token) => {
            const item = allTokens.find((item) => item.address.toLowerCase() === token.address.toLowerCase())
            if (item) newOverViews.push(item)
        })

        if (newOverViews.length === 0) setIsNoResults(true)
        else setIsNoResults(false)

        setTokensOverview(newOverViews)
    }

    function getTokenPrice(tokenSymbol: string) {
        const price = getPrice(getTokenBraavosName(tokenSymbol))
        if (!price || !price.usd) return 0
        return price.usd
    }

    function getTokenSymbol(symbol: string) {
        if (symbol === 'WSTETH') return 'wstETH'
        return symbol
    }

    function renderSearch() {
        return (
            <div className={`max-w-[400px] mb-4 flex justify-between mt-6 w-full relative ${isInputFocused ? '#0AE8F9' : 'text-[#8387A5]'} text-[16px]`}>
                <input
                    value={searchTerm}
                    onBlur={() => {
                        setIsInputFocused(false)
                    }}
                    onFocus={() => {
                        setIsInputFocused(true)
                    }}
                    onChange={(e) => {
                        setSearchTerm(e.target.value)
                    }}
                    className='focus:border-[#0AE8F9] border-[1px] rounded-[8px] px-4 py-2  border-[#8387A5]  w-full h-full mr-2 outline-none bg-transparent'
                    placeholder='Search token by name, symbol or address'
                />
                <SearchIcon className='absolute right-[16px] top-[10px] w-[18px] h-[18px]' />
            </div>
        )
    }

    const columns: Array<Column> =
        size === CustomBreakpoints.XSmall
            ? ['NAME', 'VOLUME']
            : size === CustomBreakpoints.Small
            ? ['NAME', 'PRICE', 'VOLUME']
            : size === CustomBreakpoints.Medium
            ? ['#', 'NAME', 'PRICE', 'VOLUME', 'TVL']
            : // : ['#', 'NAME', 'PRICE', 'VOLUME', 'TVL']
              ['#', 'NAME', 'PRICE', 'VOLUME', 'TVL']

    function renderEmptyTableSearch() {
        return (
            <div className='min-h-[600px] flex flex-col text-white items-center mt-[48px]'>
                <SpaceshipIcon className='w-[60px] h-[60px]' />
                <span className='mt-2 text-[18px] font-bold'>Token not found</span>
            </div>
        )
    }

    function renderContent() {
        if (isNoResults) return renderEmptyTableSearch()
        let relevantTokens = tokensOverview ? tokensOverview : []
        if (relevantTokens.length >= 10) {
            relevantTokens = relevantTokens.filter((x) => x.is_bluechip)
        }

        return (
            <TableContainer<TokenOverview & { address: string }>
                className='mb-[40px] min-h-[600px]'
                // title={'Tokens'}
                headerRow={
                    <TableHeaderRow style={{ padding: '0 16px 0px 16px' }}>
                        {columns.map(
                            (col) =>
                                ({
                                    '#': (
                                        <TableCell key={col} sx={{ minWidth: NUMBER_COLUMN_MIN_WIDTH }}>
                                            <TableHeaderText>#</TableHeaderText>
                                        </TableCell>
                                    ),
                                    NAME: (
                                        <TableCell main key={col}>
                                            <TableHeaderText>Name</TableHeaderText>
                                        </TableCell>
                                    ),
                                    PRICE: (
                                        <TableCell floatRight key={col}>
                                            <TableHeaderText>Price</TableHeaderText>
                                        </TableCell>
                                    ),
                                    // PRICE_CHANGE: (
                                    //     <TableCell floatRight key={col}>
                                    //         <TableHeaderText>Price Change</TableHeaderText>
                                    //     </TableCell>
                                    // ),
                                    VOLUME: (
                                        <TableCell floatRight key={col}>
                                            <TableHeaderText>Volume 24H</TableHeaderText>
                                        </TableCell>
                                    ),
                                    TVL: (
                                        <TableCell floatRight key={col}>
                                            <TableHeaderText>TVL</TableHeaderText>
                                        </TableCell>
                                    )
                                }[col])
                        )}
                    </TableHeaderRow>
                }
                // @ts-ignore
                items={relevantTokens}
                skeleton={<RowSkeleton columns={columns} />}
                renderItem={(token, idx) => (
                    <TableRow
                        className='cursor-pointer flex items-center lg:hover:rounded-[16px] lg:hover:bg-[#294C81]'
                        onClick={() => navigate(`/charts/token/${token.address}`)}
                        key={token.address}>
                        {columns.map(
                            (col) =>
                                ({
                                    '#': (
                                        <TableCell key={col} sx={{ minWidth: NUMBER_COLUMN_MIN_WIDTH }}>
                                            <TableText>{idx + 1}</TableText>
                                        </TableCell>
                                    ),
                                    NAME: (
                                        <TableCell main key={col}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}>
                                                <img style={{ marginRight: 8 }} src={getIconUrl('mainnet-alpha', token.address)} alt='' width={24} height={24} />
                                                <span className='text-def'>{tokensList[token.address].name}&nbsp;&nbsp;</span>
                                                <span className='text-[#0A7C9B]'>({getTokenSymbol(tokensList[token.address].symbol)})</span>
                                            </Box>
                                        </TableCell>
                                    ),
                                    PRICE: (
                                        <TableCell floatRight key={col}>
                                            <TableText>{`${
                                                tokensList[token.address].symbol === 'SPEPE' ? '<0.001$' : '$' + formatDollarValue(getTokenPrice(tokensList[token.address].symbol))
                                                // tokensList[token.address].symbol === 'SPEPE' ? '<0.001$' : '$' + formatDollarValue(getTokenPrice(tokensList[token.address].symbol))
                                            }`}</TableText>
                                        </TableCell>
                                    ),
                                    // PRICE_CHANGE: (
                                    //     <TableCell floatRight key={col}>
                                    //         {/* <TableText> */}
                                    //         <PrecentChange value={token.priceChange} />
                                    //         {/* </TableText> */}
                                    //     </TableCell>
                                    // ),
                                    VOLUME: (
                                        <TableCell floatRight key={col}>
                                            <TableText>
                                                ${formatDollarValue(token.address && getTokenOverview(token.address) ? getTokenOverview(token.address)!.volume_24h : 0)}
                                            </TableText>
                                        </TableCell>
                                    ),
                                    TVL: (
                                        <TableCell floatRight key={col}>
                                            <TableText>${formatDollarValue(token.address && getTokenOverview(token.address) ? getTokenOverview(token.address)!.tvl : 0)}</TableText>
                                        </TableCell>
                                    )
                                }[col])
                        )}
                    </TableRow>
                )}
            />
        )
    }

    return (
        <>
            <div className='flex flex-col'>
                <span className='pl-[16px] text-def font-bold text-white block'>{'Top Tokens'}</span>
                {renderSearch()}
                {renderContent()}
            </div>
        </>
    )
}

const RowSkeleton = ({ columns }: { columns: Column[] }) => {
    return (
        <TableRow>
            {columns.map(
                (col) =>
                    ({
                        '#': (
                            <TableCell key={col}>
                                <TableText>
                                    <Skeleton width={10}></Skeleton>
                                </TableText>
                            </TableCell>
                        ),
                        NAME: (
                            <TableCell main key={col}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                    <Skeleton variant='circular' width={24} height={24} sx={{ mr: 2 }} />

                                    <TableText>
                                        <Skeleton width={60} sx={{ mr: 1 }}></Skeleton>
                                    </TableText>
                                    <TableText>
                                        <Skeleton width={60}></Skeleton>
                                    </TableText>
                                </Box>
                            </TableCell>
                        ),
                        PRICE: (
                            <TableCell floatRight key={col}>
                                <TableText>
                                    <Skeleton width={60}></Skeleton>
                                </TableText>
                            </TableCell>
                        ),
                        // PRICE_CHANGE: (
                        //     <TableCell floatRight key={col}>
                        //         <TableText>
                        //             <Skeleton width={60}></Skeleton>
                        //         </TableText>
                        //     </TableCell>
                        // ),
                        VOLUME: (
                            <TableCell floatRight key={col}>
                                <TableText>
                                    <Skeleton width={60}></Skeleton>
                                </TableText>
                            </TableCell>
                        ),
                        TVL: (
                            <TableCell floatRight key={col}>
                                <TableText>
                                    <Skeleton width={60}></Skeleton>
                                </TableText>
                            </TableCell>
                        )
                    }[col])
            )}
        </TableRow>
    )
}
